import { useMemo } from "react";

import { faChartSimple } from "@fortawesome/pro-regular-svg-icons";

import { AnalyticsChart } from "src/analytics/AnalyticsChart";
import { NodeAnalyticsData, useNodeAnalyticsData } from "src/analytics/api";
import { useOpenNodeChartModal } from "src/analytics/ChartModal/store";
import {
  AddChartButton,
  getAddChartActionButton,
} from "src/analytics/common/AddChartButton";
import { SortableWrapper } from "src/analytics/common/SortableWrapper";
import {
  useDeleteNodeChart,
  useNodeCharts,
  useReorderNodeCharts,
} from "src/analytics/queries";
import { Chart } from "src/analytics/types";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { BeMappedNode } from "src/constants/NodeDataTypes";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { Skeleton } from "src/design-system/Skeleton";
import { useCapabilities } from "src/hooks/useCapabilities";
import { RunStateTrimmingBanner } from "src/nodeEditor/TrimmingReportBanner";
import { useNodeRunState } from "src/store/runState/RunState";
import { logger } from "src/utils/logger";
import { toastError } from "src/utils/toastError";

export const InsightsPane: React.FC<{ node: BeMappedNode }> = ({ node }) => {
  const { flowCharts } = useCapabilities();
  const { data: charts, isLoading: isChartsLoading } = useNodeCharts(node.id);

  const { data: analytics, isFetching } = useNodeAnalyticsData(node.id);
  const nodeRunState = useNodeRunState(node.id);

  const disabledAddChartButton = analytics?.type !== "success";

  const {
    isOpen: isDeleteChartModalOpen,
    data: chartToDelete,
    openModal: openDeleteChartModal,
    closeModal: closeDeleteChartModal,
    afterLeave: afterLeaveDeleteChartModal,
  } = useModal<Chart>();
  const openNodeChartModal = useOpenNodeChartModal();

  return (
    <div className="h-full p-6" data-loc="insights-pane">
      {(() => {
        if (isChartsLoading) {
          return <ChartPlaceholder />;
        }

        if (nodeRunState?.type === "job-run") {
          return (
            <EmptyState
              dataLoc="node-analytics-empty-state"
              description="Analytics data for test runs including Agents is currently not supported."
              headline="No analytics data available"
              icon={faChartSimple}
            />
          );
        }

        return !isChartsLoading && charts?.length === 0 ? (
          <EmptyState
            actionButton={
              flowCharts.canCreate &&
              getAddChartActionButton({
                disabled: disabledAddChartButton,
                loading: isFetching,
                onClick: () => openNodeChartModal(node.id),
              })
            }
            dataLoc="node-analytics-empty-state"
            description="Create charts to visualize node data from this Decision Flow. Charts you create will be added to this pane."
            headline="No charts created yet"
            icon={faChartSimple}
          />
        ) : (
          <div className="flex flex-col gap-y-4">
            <RunStateTrimmingBanner
              dataLoc="node-insights-trimming-banner"
              nodeIdOrAlias={node.id}
            />
            <Charts
              analytics={analytics}
              charts={charts || []}
              isLoading={isFetching}
              nodeId={node.id}
              onDeleteClick={openDeleteChartModal}
            />
            <AddChartButton
              disabled={disabledAddChartButton}
              loading={isFetching}
              size="sm"
              onClick={() => openNodeChartModal(node.id)}
            />
          </div>
        );
      })()}
      <DeleteChartModal
        afterLeave={afterLeaveDeleteChartModal}
        chart={chartToDelete}
        isOpen={isDeleteChartModalOpen}
        onClose={closeDeleteChartModal}
      />
    </div>
  );
};

const DeleteChartModal: React.FC<{
  isOpen: boolean;
  chart?: Nullable<Chart>;
  onClose: () => void;
  afterLeave: () => void;
}> = ({ chart, isOpen, onClose, afterLeave }) => {
  const { mutateAsync: deleteChart } = useDeleteNodeChart();

  const handleDelete = async () => {
    if (!chart) return;

    try {
      await deleteChart({ chartId: chart.id, resourceId: chart.resource_id });
      onClose();
    } catch (e) {
      logger.log(e);
      toastError("Failed to delete chart");
    }
  };

  return (
    <ConfirmationModal
      afterLeave={afterLeave}
      confirmationButtonText="Delete chart"
      open={isOpen}
      title={`Delete the "${chart?.title}" chart?`}
      variant="danger"
      onClose={onClose}
      onConfirm={handleDelete}
    >
      <div className="font-inter-normal-12px text-gray-500">
        Are you sure you want to delete the "{chart?.title}" chart? This will
        delete it from all versions of this flow. This action cannot be undone.
      </div>
    </ConfirmationModal>
  );
};

const Charts = ({
  analytics,
  charts,
  isLoading,
  onDeleteClick,
  nodeId,
}: {
  analytics: NodeAnalyticsData | undefined;
  charts: Chart[];
  isLoading: boolean;
  onDeleteClick: () => void;
  nodeId: string;
}) => {
  const reorderCharts = useReorderNodeCharts(nodeId);

  const order = useMemo(() => {
    return charts.map((c) => c.id);
  }, [charts]);

  const isSuccess = analytics?.type === "success";

  return (
    <SortableWrapper order={order} onChangeOrder={reorderCharts.mutateAsync}>
      {(chartsOrder) => {
        return (
          <div className="flex flex-col gap-y-4">
            {chartsOrder.map((chartId, i) => {
              const chart = charts.find((c) => c.id === chartId);

              return chart ? (
                <AnalyticsChart
                  key={chart.id}
                  chart={chart}
                  data={isSuccess ? analytics.data : undefined}
                  dataLoc={`analytics-chart-${i}`}
                  isLoading={isLoading}
                  isMultiversion={Boolean(analytics?.isMultiversion)}
                  keys={isSuccess ? analytics.keys : []}
                  onDelete={onDeleteClick}
                />
              ) : null;
            })}
          </div>
        );
      }}
    </SortableWrapper>
  );
};

const ChartPlaceholder = () => (
  <div className="flex flex-col gap-y-2.5 rounded-lg border border-gray-200 bg-white px-4 pt-3 pb-2.5">
    <Skeleton height="h-5" width="w-3/4" />
    <div className="aspect-video w-full">
      <Skeleton height="h-full" width="w-full" />
    </div>
  </div>
);
